// Set Yönetimi — /clients/kpi/setler (M16 T10 adım 3, mockup 08). Sol: müşterinin set kartları +
// "Katalogdan Set Oluştur" + Set ayarları + İşletmeler özeti; orta: SetTreeEditor (taslak →
// isDirty bar → toplu PATCH, sıralı); satır → IndicatorModal (pencere). Kaydet sonrası `refreshGrid()`.
// Sunucu formül hataları (döngü / bilinmeyen kod / sözdizimi) satıra ve çekmeceye iner.
// Birim varyantı (DR1/DR6): birim set satırının alanı değil — IndicatorModal'daki ayrı eylem; başarıda
// modal kapanır, 6 sn yeşil bant (aria-live=polite) ve satırlar YEREL güncellenir (load() yok).
// Kırılım (spec §6.3): ⋯ menüsü / IndicatorModal → BreakdownGroupModal (use-set-breakdown); grup
// satırı düzenlemesi üye ekleme kipini açar; kayıt sonrası bekleyen taslak satırları korunur.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { KpiMetric, KpiSet, KpiSetIndicator, KpiSetIndicatorPatch } from "@mahir/shared";
import {
  getKpiSet,
  listKpiMetrics,
  removeKpiSetIndicator,
  updateKpiSetIndicator,
} from "../../api/kpi";
import { ConfirmDialog } from "../../components/common/ConfirmDialog";
import { ErrorState, LoadingState } from "../../components/common/StateSurface";
import { AddIndicatorPicker } from "../../components/kpi/AddIndicatorPicker";
import { CatalogSyncPanel } from "../../components/kpi/CatalogSyncPanel";
import { ForkSetModal } from "../../components/kpi/ForkSetModal";
import type { FormulaServerError } from "../../components/kpi/FormulaEditor";
import { IndicatorModal } from "../../components/kpi/IndicatorModal";
import type {
  CatalogUnitInfo,
  IndicatorUnitApplied,
} from "../../components/kpi/IndicatorUnitSection";
import { KpiEmptyState } from "../../components/kpi/KpiEmptyState";
import { KpiScreen } from "../../components/kpi/KpiScreen";
import { EntitiesSummaryCard, SetSettingsPanel } from "../../components/kpi/SetSettingsPanel";
import { SetListCards } from "../../components/kpi/SetListCards";
import { SetTreeEditor } from "../../components/kpi/SetTreeEditor";
import { btnPrimary } from "../../components/kpi/kpi-form-styles";
import { setBreakdownView } from "../../lib/kpi-breakdown-set";
import {
  buildIndicatorTree,
  diffIndicatorPatches,
  moveAmongSiblings,
} from "../../lib/kpi-set-editor";
import { useLegacyCategoryValues } from "../../hooks/use-legacy-category-values";
import { calcGroupChanged } from "../../lib/calc-group-change";
import { describeSetChanges } from "../../lib/kpi-set-changes";
import { applyOrderChanges } from "../../lib/kpi-reorder";
import { unitSuccessMessage } from "../../lib/kpi-unit-change-flow";
import { KPI_COPY, kpiSetManagementHref } from "../../lib/kpi-ui";
import { kpiConvertLegacyHref, kpiMetricLink } from "../../lib/kpi-unit-links";
import { buildChildrenByCode } from "../../lib/kpi-wavg-pairs";
import { useAuthStore } from "../../stores/auth-store";
import { useKpiStore } from "../../stores/kpi-store";
import { toServerError } from "./set-server-error";
import { useSetBreakdown } from "./use-set-breakdown";
import { guardedNavigate, useUnsavedNavGuard } from "./use-unsaved-nav-guard";
import { useForkPackageDeeplink } from "../../hooks/use-fork-package-deeplink";

export function KpiSetsPage(): JSX.Element {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canEdit = user?.permissions["kpi.manage"] === true;
  const clientId = useKpiStore((s) => s.clientId);
  const setId = useKpiStore((s) => s.setId);
  const sets = useKpiStore((s) => s.sets);
  const clientName = useKpiStore((s) => s.clients.find((c) => c.id === s.clientId)?.name ?? "");
  const clientSector = useKpiStore((s) => s.clients.find((c) => c.id === s.clientId)?.sector);

  const [set, setSet] = useState<KpiSet | null>(null);
  const [original, setOriginal] = useState<KpiSetIndicator[]>([]);
  const [draft, setDraft] = useState<KpiSetIndicator[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState<Record<string, FormulaServerError>>({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [fork, setFork] = useState<{
    open: boolean;
    clientId: string | null;
    packageId?: string | null;
  }>({ open: false, clientId: null });
  // K-B10 / TODO-KPI-141: derin bağlantı Set kur penceresini (paket ön-seçili) bir kez açar
  const deeplink = useForkPackageDeeplink();
  useEffect(() => {
    if (!deeplink.pending || !canEdit) return;
    setFork({ open: true, clientId: deeplink.clientId, packageId: deeplink.packageId });
    deeplink.consume();
  }, [deeplink.pending, canEdit]);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [pendingSwitch, setPendingSwitch] = useState<string | null>(null);
  /** P3-28: kaydedilmemiş değişiklikle uygulama içi linke tıklanınca onay (hedef yol) */
  const [pendingNav, setPendingNav] = useState<string | null>(null);
  const [flashState, setFlashState] = useState<{ text: string; ms: number } | null>(null);
  const flash = flashState?.text ?? null;
  const setFlash = (text: string | null, ms = 4000): void =>
    setFlashState(text ? { text, ms } : null);
  /** birim varyantı: metrik id → katalog varsayılan birimi + alternatifleri (IndicatorModal seçicisi) */
  const [catalogUnits, setCatalogUnits] = useState<ReadonlyMap<string, CatalogUnitInfo> | null>(
    null,
  );
  /** kırılım modalı: katalog grupları/üyeleri (aynı istek); grup eklenince yeniden okunur */
  const [catalog, setCatalog] = useState<KpiMetric[] | null>(null);
  const [catalogTick, setCatalogTick] = useState(0);

  useEffect(() => {
    setCatalogUnits(null);
    if (!clientId) return;
    let live = true;
    listKpiMetrics({ clientId }).then(
      (res) => {
        if (!live) return;
        const m = new Map<string, CatalogUnitInfo>();
        for (const x of res.metrics)
          m.set(x.id, { defaultUnit: x.defaultUnit, unitVariants: x.unitVariants ?? [] });
        setCatalogUnits(m);
        setCatalog(res.metrics);
      },
      () => undefined, // katalog okunamazsa seçici göstergenin kendi birimine düşer
    );
    return () => {
      live = false;
    };
  }, [clientId, catalogTick]);

  const load = useCallback(async () => {
    if (!setId) {
      setSet(null);
      setOriginal([]);
      setDraft([]);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const res = await getKpiSet(setId);
      setSet(res.set);
      setOriginal(res.indicators);
      setDraft(res.indicators);
      setFailed({});
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Veri seti yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [setId]);

  useEffect(() => {
    setSelectedId(null);
    void load();
  }, [load]);

  useEffect(() => {
    if (!flashState) return;
    const t = setTimeout(() => setFlashState(null), flashState.ms);
    return () => clearTimeout(t);
  }, [flashState]);

  const patches = useMemo(() => diffIndicatorPatches(original, draft), [original, draft]);
  const dirtyCount = patches.length;
  // Finar rev-B 24 — kaydet çubuğundaki "N değişiklik" listesi (aynı orijinal ↔ taslak farkı)
  const changes = useMemo(() => describeSetChanges(original, draft), [original, draft]);
  const legacyValues = useLegacyCategoryValues(setId, original);
  const calcChanges = useMemo(() => calcGroupChanged(original, draft), [original, draft]);

  // P3-28: kaydedilmemiş set değişikliği varken sekme kapatma/yenileme ve uygulama içi gezinme
  useUnsavedNavGuard(dirtyCount, setPendingNav);
  const tree = useMemo(() => buildIndicatorTree(draft), [draft]);
  const bv = useMemo(() => setBreakdownView(draft), [draft]);
  /** yapı satırı (kategori bandı / kırılım grubu) — gösterge alanları yok */
  const isStructural = (id: string): boolean =>
    ["category", "breakdown_group"].includes(bv.kindOf(id));
  const setCodes = useMemo(() => new Set(draft.map((i) => i.code)), [draft]);
  const setLabels = useMemo(
    () => new Map(draft.map((i) => [i.code, i.labelOverride ?? i.label] as const)),
    [draft],
  );
  // TODO-KPI-91: AGIRLIKLI_ORT eşleme önizlemesi — taslak sırası (orderIndex), etiket override ?? metrik
  const setChildren = useMemo(
    () => buildChildrenByCode(draft, (i) => i.labelOverride ?? i.label),
    [draft],
  );
  const setUnits = useMemo(() => new Map(draft.map((i) => [i.code, i.unit] as const)), [draft]);
  // TODO-KPI-32: ağırlık adayları — kategori olmayan, metin olmayan set göstergeleri (metrik id)
  const weightOptions = useMemo(
    () =>
      draft
        .filter(
          (i) =>
            i.valueType !== "text" && !["category", "breakdown_group"].includes(bv.kindOf(i.id)),
        )
        .map((i) => ({
          value: i.metricId,
          label: i.labelOverride ?? i.label,
          code: i.code,
          hint: i.unit ?? "",
        })),
    [draft, bv],
  );
  const selected = draft.find((i) => i.id === selectedId) ?? null;
  const failedIds = useMemo(() => new Set(Object.keys(failed)), [failed]);

  function patchDraft(id: string, patch: Partial<KpiSetIndicator>): void {
    setDraft((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
    // Önceki Kaydet'te sunucunun reddettiği satır düzenlenmeye başlayınca eski hata ve
    // "kaydedilemedi" rozeti kalkar (manuel test bulgusu — düzeltme sonrası hata asılı kalıyordu).
    setFailed((f) => {
      if (!(id in f)) return f;
      const { [id]: _dropped, ...rest } = f;
      return rest;
    });
  }

  // ↑↓ ve sürükle-bırak aynı taslak yolu (rev-2 #4b/#12b/#14) — Kaydet `patches` ile gönderir
  function applyOrder(changes: ReadonlyMap<string, number> | null): void {
    if (changes && changes.size > 0) setDraft((prev) => applyOrderChanges(prev, changes));
  }

  async function save(): Promise<void> {
    if (patches.length === 0) return;
    setSaving(true);
    const errs: Record<string, FormulaServerError> = {};
    const savedIds = new Set<string>();
    let converted = 0;
    for (const p of patches) {
      try {
        const r = await updateKpiSetIndicator(p.id, p.patch as KpiSetIndicatorPatch);
        converted += r.converted;
        savedIds.add(p.id);
      } catch (err) {
        errs[p.id] = toServerError(err);
      }
    }
    setFailed(errs);
    // TODO-KPI-112: hesap grubu seçimi ham değerleri raporlanana çevirdiyse kalıcı bant (kapatılana dek)
    if (converted > 0 && setId) {
      useKpiStore.setState({ breakdownConverted: { setId, values: converted } });
    }
    const failedCount = Object.keys(errs).length;
    if (failedCount === 0) {
      setFlash(`${savedIds.size} gösterge kaydedildi`);
      await load();
    } else {
      // başarılı satırlar orijinale alınır; hatalı satırlar taslakta kalır (kullanıcı düzeltir)
      try {
        const res = await getKpiSet(setId!);
        setSet(res.set);
        setOriginal(res.indicators);
        setDraft((prev) =>
          res.indicators.map((fresh) =>
            errs[fresh.id] ? (prev.find((d) => d.id === fresh.id) ?? fresh) : fresh,
          ),
        );
      } catch {
        // yükleme hatası — taslak olduğu gibi kalır
      }
      const firstFailed = Object.keys(errs)[0];
      if (firstFailed) setSelectedId(firstFailed);
      setFlash(`${savedIds.size} kaydedildi · ${failedCount} satır kaydedilemedi`);
    }
    setSaving(false);
    const store = useKpiStore.getState();
    await store.refreshGrid();
    void store.loadClients();
  }

  /** Kırılım kaydı sonrası: sunucu satırları alınır, kaydedilmemiş taslak alanları korunur. */
  async function reloadKeepingDraft(): Promise<void> {
    if (!setId) return;
    const dirty = new Map(patches.map((p) => [p.id, p.patch as Partial<KpiSetIndicator>]));
    const res = await getKpiSet(setId);
    setSet(res.set);
    setOriginal(res.indicators);
    setDraft(res.indicators.map((f) => (dirty.has(f.id) ? { ...f, ...dirty.get(f.id) } : f)));
  }
  const breakdown = useSetBreakdown({
    setId,
    clientId,
    clientName,
    draft,
    view: bv,
    catalog,
    canManageCategories: user?.permissions["kpi.catalog.categories"] === true,
    onSaved: async (message) => {
      setFlash(message);
      setCatalogTick((n) => n + 1);
      await reloadKeepingDraft().catch(() => undefined);
    },
  });
  /** satır seçimi: kırılım grubu → üye ekleme kipi (grup IndicatorModal açmaz) */
  function selectRow(id: string | null): void {
    const total = id && bv.kindOf(id) === "breakdown_group" ? bv.totalOf(id) : null;
    if (id && total) breakdown.open({ totalId: total.id, groupId: id });
    else setSelectedId(id);
  }

  /** DR6 — birim uygulandı: satırlar yerel (orijinal + taslak, aynı metrik), bant 6 sn, modal kapanır */
  function onUnitApplied(r: IndicatorUnitApplied): void {
    const label = draft.find((i) => i.metricId === r.metricId)?.label ?? "";
    const fix = (list: KpiSetIndicator[]): KpiSetIndicator[] =>
      list.map((i) =>
        i.metricId === r.metricId ? { ...i, unit: r.unit, unitSource: r.unitSource } : i,
      );
    setOriginal(fix);
    setDraft(fix);
    setSelectedId(null);
    setFlash(unitSuccessMessage(r, label, clientName), 6000);
    void useKpiStore.getState().refreshGrid(); // çevrilen değerler Veri Girişi tablosunda
  }

  /** DR5 engel bağlantıları — kaydedilmemiş taslak varken bildik "ayrıl?" onayından geçer */
  function go(dest: string): void {
    guardedNavigate(dirtyCount, dest, setPendingNav, (d) => void navigate(d));
  }
  const metricIdOf = (code: string): string | null =>
    draft.find((i) => i.code === code)?.metricId ?? null;
  const unitActions = {
    onOpenFormula: (code: string, sid: string | null) => {
      const hit = draft.find((i) => i.code === code);
      if ((sid === null || sid === setId) && hit) setSelectedId(hit.id);
      else if (sid) go(kpiSetManagementHref(clientId, sid));
    },
    // TODO-KPI-96: bağlantı göstergeyi taşır (kod → metricId; set dışı gösterge süzgeçsiz açar)
    onInspectValues: (code: string) => go(kpiMetricLink("/clients/kpi/tablo", metricIdOf(code))),
    onInspectReported: (code: string) => go(kpiMetricLink("/clients/kpi/tablo", metricIdOf(code))),
    onInspectTargets: (code: string) =>
      go(kpiMetricLink("/clients/kpi/hedefler", metricIdOf(code))),
  };

  async function removeIndicator(id: string): Promise<void> {
    setConfirmRemove(null);
    try {
      const res = await removeKpiSetIndicator(id);
      setFlash(`${res.removed} gösterge veri setinden kaldırıldı`);
      if (selectedId === id) setSelectedId(null);
      await load();
      const store = useKpiStore.getState();
      await store.refreshGrid();
      void store.loadClients();
      // sol sütundaki set kartı (N gösterge) store `sets`'ten okur — üyelik değişince yenile
      void store.reloadSets();
    } catch (err) {
      setFlash(err instanceof Error ? err.message : "Kaldırılamadı");
    }
  }

  function switchSet(id: string): void {
    if (id === setId) return;
    if (dirtyCount > 0) setPendingSwitch(id);
    else void useKpiStore.getState().selectSet(id);
  }

  const openFork = (cid?: string | null): void =>
    setFork({ open: true, clientId: cid ?? clientId });

  return (
    <KpiScreen
      title={KPI_COPY.titles.setler}
      requireSet={false}
      scrollBody // uzun ağaçta yapışkan kaydet çubuğu görünür kalsın (KpiScreen notu)
      onCreateSet={() => openFork()}
      onCreateSetForClient={(id) => openFork(id)}
      // Kaydedilmemiş ağaç taslağı varken üst çubuktan müşteri/set değişimi onay ister
      // (manuel test bulgusu 3.7 — set listesi ve sol menü korunuyordu, bağlam çubuğu değil)
      lock={dirtyCount > 0}
      lockTitle={KPI_COPY.contextLockTitle}
      lockBody={`Kaydedilmemiş veri seti değişikliği var (${dirtyCount} gösterge). Müşteri ya da veri setini değiştirirseniz bu değişiklikler kaybolur — önce ağacın altındaki "${KPI_COPY.setChanges.save}" düğmesine basın.`}
    >
      {!clientId ? <KpiEmptyState onCreateSet={(id) => openFork(id)} /> : null}
      {clientId && flash ? (
        <div
          role="status"
          aria-live="polite"
          className="text-sm rounded-lg px-3 py-2 mb-3"
          style={{
            background: "color-mix(in srgb, var(--color-success) 12%, white)",
            color: "var(--color-success)",
          }}
        >
          {flash}
        </div>
      ) : null}
      {clientId ? (
        // Gösterge düzenleme modal'da (2026-10-08) — ağaç paneli her zaman tam genişlik
        <div className="kpi-sets-grid" data-drawer="false">
          {/* Sol sütun */}
          <div className="grid gap-3">
            {canEdit ? (
              <button
                type="button"
                style={{ ...btnPrimary, width: "100%" }}
                onClick={() => openFork()}
              >
                {KPI_COPY.emptyCreateSet}
              </button>
            ) : null}
            <SetListCards sets={sets} setId={setId} onSwitch={switchSet} />
            {set && clientId ? (
              <>
                <SetSettingsPanel
                  set={set}
                  canEdit={canEdit}
                  clientSector={clientSector}
                  onUpdated={(u) => {
                    setSet(u);
                    setFlash("Veri seti ayarları kaydedildi");
                    void useKpiStore.getState().loadClients();
                    // TODO-KPI-29: set kartı + üst bar etiketi store `sets`'ten okur — ad/çerçeve
                    // değişince liste de yenilensin (önce yalnız müşteri özeti yenileniyordu).
                    void useKpiStore.getState().reloadSets();
                  }}
                  onDeleted={() => {
                    setFlash("Veri seti silindi");
                    void useKpiStore.getState().selectClient(clientId);
                  }}
                />
                <EntitiesSummaryCard clientId={clientId} />
              </>
            ) : null}
          </div>

          {/* Orta: ağaç editörü */}
          <div
            className="rounded-xl p-4"
            style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
          >
            {!setId ? (
              <div className="text-sm p-4" style={{ color: "var(--color-text-2)" }}>
                {sets.length === 0
                  ? `${KPI_COPY.noSetForClient}. "${KPI_COPY.emptyCreateSet}" ile başlayın.`
                  : "Soldan bir veri seti seçin."}
              </div>
            ) : loading && draft.length === 0 ? (
              <LoadingState />
            ) : loadError ? (
              <ErrorState message={loadError} onRetry={() => void load()} />
            ) : (
              <>
                {user ? (
                  // TODO-KPI-03: katalogdan ayrışan override/formül + kategoriye eklenen yeni göstergeler
                  <CatalogSyncPanel
                    setId={setId}
                    userId={user.id}
                    canEdit={canEdit}
                    blocked={dirtyCount > 0}
                    onOpenIndicator={setSelectedId}
                    catalog={catalog}
                    onApplied={(msg) => {
                      setFlash(msg);
                      void load().then(() => {
                        const store = useKpiStore.getState();
                        void store.refreshGrid();
                        void store.loadClients();
                        void store.reloadSets(); // yeni gösterge eklenmiş olabilir — set kartı sayacı
                      });
                    }}
                  />
                ) : null}
                {breakdown.band}
                <SetTreeEditor
                  indicators={draft}
                  setUnits={setUnits}
                  selectedId={selectedId}
                  dirtyCount={dirtyCount}
                  changes={changes}
                  calcGroupChanges={calcChanges}
                  legacyValueMetricIds={legacyValues}
                  onConvertLegacy={(id) => go(kpiConvertLegacyHref(id))}
                  saving={saving}
                  canEdit={canEdit}
                  failedIds={failedIds}
                  onSelect={selectRow}
                  onMove={(id, d) => applyOrder(moveAmongSiblings(tree, id, d))}
                  onReorder={applyOrder}
                  onToggleClientOpen={(id, open) =>
                    patchDraft(id, { clientAccess: open ? "open" : "closed" })
                  }
                  onSave={() => void save()}
                  onDiscard={() => {
                    setDraft(original);
                    setFailed({});
                  }}
                  onAdd={() => setPickerOpen(true)}
                  onAddBreakdownGroup={breakdown.addGroup}
                  onAddBreakdownMember={(id) => selectRow(id)}
                  onRemoveRow={(id) => setConfirmRemove(id)}
                  flashIds={breakdown.flashIds}
                />
              </>
            )}
          </div>
        </div>
      ) : null}

      {selected ? (
        <IndicatorModal
          key={selected.id}
          indicator={selected}
          clientId={clientId}
          clientName={clientName}
          catalogUnit={catalogUnits?.get(selected.metricId) ?? null}
          onUnitApplied={onUnitApplied}
          {...unitActions}
          isCategory={isStructural(selected.id)}
          breakdownView={bv}
          onEditBreakdownInCatalog={() => go("/clients/kpi/katalog")}
          onAddBreakdownGroup={breakdown.addGroupFor(selected.id, () => setSelectedId(null))}
          setCodes={setCodes}
          setUnits={setUnits}
          setLabels={setLabels}
          setChildren={setChildren}
          weightOptions={weightOptions}
          catalog={catalog}
          promoteLocked={dirtyCount > 0}
          onPromoted={() => {
            setCatalogTick((t) => t + 1); // kod/kapsam değişti — katalog + set + ızgara tazelenir
            void load().then(() => useKpiStore.getState().refreshGrid());
          }}
          canEdit={canEdit}
          serverError={failed[selected.id] ?? null}
          onChange={(patch) => patchDraft(selected.id, patch)}
          onRemove={() => setConfirmRemove(selected.id)}
          onClose={() => setSelectedId(null)}
        />
      ) : null}

      {setId && clientId ? (
        <AddIndicatorPicker
          open={pickerOpen}
          setId={setId}
          clientId={clientId}
          indicators={draft}
          onClose={() => setPickerOpen(false)}
          onAdded={() => {
            void load().then(() => {
              const store = useKpiStore.getState();
              void store.refreshGrid();
              void store.loadClients();
              void store.reloadSets(); // set kartı sayacı
            });
          }}
        />
      ) : null}

      {breakdown.modal}

      <ForkSetModal
        open={fork.open}
        initialClientId={fork.clientId}
        initialPackageId={fork.packageId ?? null}
        onClose={() => setFork({ open: false, clientId: null })}
      />

      <ConfirmDialog
        open={confirmRemove !== null}
        title="Gösterge veri setinden kaldırılsın mı?"
        body="Yalnız veri seti üyeliği silinir; girilmiş değerler havuzda kalır ve başka bir veri setine eklendiğinde yeniden görünür. Kategori kaldırılırsa alt göstergeleri de veri setinden çıkar."
        confirmLabel="Kaldır"
        tone="danger"
        onConfirm={() => {
          if (confirmRemove) void removeIndicator(confirmRemove);
        }}
        onCancel={() => setConfirmRemove(null)}
      />

      <ConfirmDialog
        open={pendingNav !== null}
        title={KPI_COPY.contextLockTitle}
        body="Sayfadan ayrılırsanız kaydedilmemiş gösterge değişiklikleri kaybolur."
        confirmLabel="Ayrıl"
        tone="danger"
        onConfirm={() => {
          const dest = pendingNav;
          setPendingNav(null);
          if (dest) {
            setDraft(original); // guard tekrar tetiklenmesin
            void navigate(dest);
          }
        }}
        onCancel={() => setPendingNav(null)}
      />

      <ConfirmDialog
        open={pendingSwitch !== null}
        title={KPI_COPY.contextLockTitle}
        body="Başka veri setine geçerseniz kaydedilmemiş gösterge değişiklikleri kaybolur."
        confirmLabel={KPI_COPY.contextLockConfirm}
        tone="danger"
        onConfirm={() => {
          const id = pendingSwitch;
          setPendingSwitch(null);
          if (id) void useKpiStore.getState().selectSet(id);
        }}
        onCancel={() => setPendingSwitch(null)}
      />
    </KpiScreen>
  );
}
